/**
 * DecisionResult.jsx — Week 3 Fix + Dark Mode + Scrollable Modal (max-h-[90vh])
 */
import { motion, AnimatePresence } from "framer-motion";
import {
  FiCheckCircle, FiPauseCircle, FiXCircle,
  FiTrendingUp, FiTrendingDown, FiDollarSign,
  FiTarget, FiShare2, FiDownload, FiX,
  FiClock, FiInfo, FiBarChart2, FiZap, FiLoader,
} from "react-icons/fi";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useLanguage } from "../../contexts/LanguageContext";
import { usePDFExport } from "../../hooks/usePDFExport";
import { useDarkMode } from "../../hooks/useDarkMode";

const NEXT_ACTIONS_AR = {
  'Scale budget 20-30% to maximize returns':      'زِد الميزانية 20-30% لتعظيم العائد',
  'Continue A/B testing creatives':               'استمر في اختبار A/B للإعلانات',
  'Monitor daily and adjust bids':                'راقب يومياً وعدّل العروض',
  'Monitor top-performing products':              'راقب المنتجات الأعلى أداءً',
  'Implement upselling strategies':               'طبّق استراتيجيات البيع الإضافي',
  'Review inventory levels':                      'راجع مستويات المخزون',
  'Optimize lead generation channels':            'حسّن قنوات توليد العملاء المحتملين',
  'Invest in customer retention':                 'استثمر في الاحتفاظ بالعملاء',
  'Track LTV by segment':                         'تتبع القيمة الدائمة حسب الشريحة',
  'Pause campaign and analyze data':              'أوقف الحملة مؤقتاً وحلّل البيانات',
  'Review targeting and audience':                'راجع الاستهداف والجمهور',
  'Test new creatives before resuming':           'اختبر إعلانات جديدة قبل الاستئناف',
  'Evaluate budget allocation':                   'قيّم توزيع الميزانية',
  'Stop campaign immediately':                    'أوقف الحملة فوراً',
  'Conduct post-mortem analysis':                 'أجرِ تحليلاً شاملاً للأداء',
  'Revise strategy before relaunch':              'راجع الاستراتيجية قبل إعادة الإطلاق',
  'Analyze what failed for future campaigns':     'حلّل أسباب الفشل للحملات المستقبلية',
};

function getLocalizedReason(decision, lang) {
  if (lang === 'ar') return decision.reasonAr || decision.reason || '';
  return decision.reason || '';
}

// ── G1/G2/G3 resolvers ──────────────────────────────────
// New decisions carry pathBadge / kpisDisplay / inputsSummary from the
// backend. Older saved decisions don't — so we fall back gracefully to
// the legacy kpis object + KPI_SKIP filtering when the new fields are
// absent. This keeps historical decisions from breaking.

function getPathBadge(decision, lang) {
  if (!decision.pathBadge) return null;
  return lang === 'ar' ? decision.pathBadge.labelAr : decision.pathBadge.label;
}

function getInputsSummary(decision, lang) {
  if (!Array.isArray(decision.inputsSummary) || decision.inputsSummary.length === 0) return [];
  return decision.inputsSummary.map(i => ({
    label: lang === 'ar' ? i.labelAr : i.label,
    value: i.value,
    unit:  i.unit || '',
  }));
}

function getTargetNotice(decision, lang) {
  if (!decision.targetNotice) return null;
  return lang === 'ar' ? (decision.targetNoticeAr || decision.targetNotice) : decision.targetNotice;
}

// Returns [{ key, label, value, unit }] — prefers backend kpisDisplay
// (already null-filtered + deduped + path-aware), else legacy fallback.
function getDisplayKpis(decision, lang, t) {
  if (Array.isArray(decision.kpisDisplay) && decision.kpisDisplay.length > 0) {
    return decision.kpisDisplay.map(k => ({
      key:   k.key,
      label: lang === 'ar' ? k.labelAr : k.label,
      value: k.value,
      unit:  k.unit || '',
      fromBackend: true,
    }));
  }
  // Legacy fallback: old kpis object, skip internal/meta + nulls
  if (!decision.kpis) return [];
  return Object.entries(decision.kpis)
    .filter(([k, v]) => v !== null && v !== undefined && !KPI_SKIP.has(k))
    .map(([k, v]) => ({ key: k, label: kpiLabel(k, t), value: v, unit: '', fromBackend: false }));
}
function getLocalizedActions(decision, lang) {
  if (lang === 'ar') {
    if (decision.nextActionsAr?.length > 0) return decision.nextActionsAr;
    return (decision.nextActions || []).map(a => NEXT_ACTIONS_AR[a] || a);
  }
  return decision.nextActions || [];
}

const VERDICT = {
  CONTINUE: { icon: <FiCheckCircle />, ringCls: "border-green-500 bg-green-500/10",  iconCls: "text-green-400",  textCls: "text-green-400",  badgeCls: "bg-green-500/20 text-green-400 border-green-500/40",   glowCls: "shadow-green-500/20"  },
  ADJUST:   { icon: <FiPauseCircle />, ringCls: "border-yellow-500 bg-yellow-500/10", iconCls: "text-yellow-400", textCls: "text-yellow-400", badgeCls: "bg-yellow-500/20 text-yellow-400 border-yellow-500/40", glowCls: "shadow-yellow-500/20" },
  STOP:     { icon: <FiXCircle />,     ringCls: "border-red-500 bg-red-500/10",       iconCls: "text-red-400",    textCls: "text-red-400",    badgeCls: "bg-red-500/20 text-red-400 border-red-500/40",         glowCls: "shadow-red-500/20"    },
};

// Status badge labels — new 3-value system from spec
const STATUS_LABELS = {
  en: { Stable: "Stable", Opportunity: "Opportunity", Critical: "Critical",
        // backward compat
        Excellent: "Excellent", Good: "Good", Fair: "Fair", Poor: "Poor" },
  ar: { Stable: "مستقر", Opportunity: "فرصة", Critical: "حرج",
        Excellent: "ممتاز", Good: "جيد", Fair: "مقبول", Poor: "ضعيف" },
};
function getStatusLabel(status, lang) {
  return (STATUS_LABELS[lang] || STATUS_LABELS.en)[status] || status;
}
// Status badge colour override (status drives colour, not just decision)
function getStatusBadgeCls(status) {
  if (status === "Stable"      || status === "Excellent" || status === "Good")
    return "bg-green-500/20 text-green-400 border-green-500/40";
  if (status === "Opportunity" || status === "Fair")
    return "bg-yellow-500/20 text-yellow-400 border-yellow-500/40";
  if (status === "Critical"    || status === "Poor")
    return "bg-red-500/20 text-red-400 border-red-500/40";
  return "bg-gray-500/20 text-gray-400 border-gray-500/40";
}

// KPI keys to skip in the display grid (internal/meta fields)
const KPI_SKIP = new Set([
  'spend','revenue','leads','conversions','targetCPL','targetCPA',
  'grossProfit','returnsLoss','profitWarning','profitWarningMsg',
  'revenueEst','revenueActual','ltv','ltvToCac','roi',
  'cpm','conversionRate','avgTicket','fees','shipping',
]);

const KPI_META = {
  roas:            { icon: <FiTrendingUp />, color: "text-blue-400",   fmt: v => `${v.toFixed(2)}x`,             good: v => v >= 1.5  },
  profit:          { icon: <FiDollarSign />, color: "text-green-400",  fmt: v => `${v.toFixed(2)} ر.س`, good: v => v > 0     },
  cpl:             { icon: <FiTarget />,     color: "text-purple-400", fmt: v => `${v.toFixed(2)} ر.س`,           good: v => v < 50    },
  cac:             { icon: <FiDollarSign />, color: "text-orange-400", fmt: v => `${v.toFixed(2)} ر.س`,           good: v => v < 200   },
  cpa:             { icon: <FiTarget />,     color: "text-pink-400",   fmt: v => `${v.toFixed(2)} ر.س`,           good: v => v < 100   },
  ctr:             { icon: <FiTrendingUp />, color: "text-green-400",  fmt: v => `${v.toFixed(2)}%`,              good: v => v >= 2    },
  cpc:             { icon: <FiDollarSign />, color: "text-yellow-400", fmt: v => `${v.toFixed(2)} ر.س`,           good: v => v < 3     },
  customers:       { icon: <FiTarget />,     color: "text-blue-400",   fmt: v => `${Math.round(v)}`,              good: v => v > 0     },
  conversionPct:   { icon: <FiTrendingUp />, color: "text-teal-400",   fmt: v => `${v.toFixed(1)}%`,              good: v => v >= 10   },
  conversion_rate: { icon: <FiTarget />,     color: "text-blue-400",   fmt: v => `${v.toFixed(1)}%`,              good: v => v >= 5    },
  profitMargin:    { icon: <FiTrendingUp />, color: "text-green-400",  fmt: v => `${v.toFixed(1)}%`,              good: v => v >= 20   },
  returnRate:      { icon: <FiTrendingDown />, color: "text-red-400",  fmt: v => `${v.toFixed(1)}%`,              good: v => v < 10    },
};

function formatKPI(key, val) {
  if (typeof val !== "number") return String(val);
  const m = KPI_META[key];
  if (m) return m.fmt(val);
  if (key.includes("rate") || key.includes("ctr") || key.includes("Pct") || key.includes("pct")) return `${val.toFixed(1)}%`;
  if (key.includes("roas"))                         return `${val.toFixed(2)}x`;
  if (key.includes("profit") || key.includes("cpl") || key.includes("cac") || key.includes("cpa") || key.includes("cpc"))
    return `${val.toFixed(2)} ر.س`;
  if (key === "customers")                           return `${Math.round(val)}`;
  return val.toFixed(2);
}
function kpiLabel(key, t) {
  const labels = {
    roas:          "العائد على الإنفاق الإعلاني",
    profit:        "الربح",
    cpl:           "تكلفة العميل المحتمل",
    cac:           "تكلفة اكتساب العميل",
    cpa:           "تكلفة الاكتساب",
    ctr:           "CTR",
    cpc:           "تكلفة النقرة",
    customers:     "العملاء الدافعون",
    conversionPct: "معدل التحويل",
    conversion_rate: (typeof t === 'function' ? t("conversion_rate") : "معدل التحويل"),
    profitMargin:  "هامش الربح",
    returnRate:    "معدل الإرجاع",
  };
  return labels[key] || key.replace(/_/g, " ").toUpperCase();
}

const DecisionResult = ({ decision, campaign, onShare, onClose, onDownloadPDF }) => {
  const { canShare, canDownloadPDF } = useFeatureFlags();
  const { t, isRTL, lang } = useLanguage();
  const { generatePDF, generating: pdfGenerating } = usePDFExport();
  const { darkMode } = useDarkMode();

  const handlePDF = async () => {
    if (onDownloadPDF) { onDownloadPDF(); return; }
    await generatePDF(decision, campaign || null, lang);
  };

  const verdict    = VERDICT[decision.decision] || VERDICT.CONTINUE;

  // G1/G2/G3 — resolved display values (backend fields preferred, legacy fallback)
  const pathBadge     = getPathBadge(decision, lang);
  const inputsSummary = getInputsSummary(decision, lang);
  const targetNotice  = getTargetNotice(decision, lang);
  const displayKpis   = getDisplayKpis(decision, lang, t);

  // Format a KPI value for display. Backend rows carry a unit; legacy rows
  // are formatted by the existing key-based formatter.
  const formatDisplayValue = (row) => {
    if (!row.fromBackend) return formatKPI(row.key, row.value);
    if (typeof row.value !== "number") return String(row.value);
    if (row.unit === "x")   return `${row.value.toFixed(2)}x`;
    if (row.unit === "%")   return `${row.value.toFixed(1)}%`;
    if (row.unit === "SAR") return `${row.value.toFixed(2)} ر.س`;
    if (row.key === "customers") return `${Math.round(row.value)}`;
    return `${row.value}`;
  };

  return (
    <AnimatePresence>
      {/* Overlay — centres the modal, NO overflow here */}
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          /* ↓ KEY CHANGE: max height + scroll lives on the modal box itself */
          className={`
            ${darkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}
            border rounded-2xl w-full max-w-3xl shadow-2xl ${verdict.glowCls}
            max-h-[90vh] overflow-y-auto flex flex-col
          `}
        >
          {/* ══ VERDICT HEADER — stays at top while body scrolls ══ */}
          <div className={`
            relative overflow-hidden px-8 pt-8 pb-6 flex-shrink-0
            ${verdict.ringCls} border-b ${darkMode ? "border-gray-700/60" : "border-gray-200"}
          `}>
            <div className={`absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl opacity-20 ${verdict.ringCls}`} />

            <div className="flex items-center gap-5">
              <motion.div
                initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", delay: 0.15, stiffness: 200 }}
                className={`${verdict.ringCls} border-2 rounded-2xl w-20 h-20 flex items-center justify-center flex-shrink-0 text-5xl ${verdict.iconCls}`}
              >
                {verdict.icon}
              </motion.div>

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h2 className={`text-4xl font-black ${verdict.textCls}`}>
                    {t(`decision_label_${decision.decision?.toLowerCase()}`) || decision.decision}
                  </h2>
                  {decision.status && (
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusBadgeCls(decision.status)}`}>
                      {getStatusLabel(decision.status, lang)}
                    </span>
                  )}
                  {pathBadge && (
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border inline-flex items-center gap-1 ${
                      darkMode ? "bg-blue-500/15 text-blue-300 border-blue-500/40" : "bg-blue-50 text-blue-700 border-blue-200"
                    }`}>
                      <FiTarget className="text-[10px]" />
                      {pathBadge}
                    </span>
                  )}
                  {decision.kpis?.profitWarning && (
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                      darkMode ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : "bg-amber-50 text-amber-700 border-amber-200"
                    }`}>
                      {lang === "ar" ? "العائد فقط (بدون هامش)" : "ROAS only (no margin)"}
                    </span>
                  )}
                </div>
                {decision.nextReviewDays && (
                  <div className={`flex items-center gap-1.5 text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                    <FiClock className="text-xs flex-shrink-0" />
                    <span>{t("next_review")}:{" "}
                      <span className={`${darkMode ? "text-white" : "text-gray-900"} font-medium`}>
                        {decision.nextReviewDays} {t("days")}
                      </span>
                    </span>
                  </div>
                )}
              </div>

              <button onClick={onClose}
                className={`absolute top-4 right-4 p-2 rounded-xl transition-colors
                  ${darkMode ? "text-gray-500 hover:text-white hover:bg-gray-700/60" : "text-gray-400 hover:text-gray-900 hover:bg-gray-100"}`}>
                <FiX />
              </button>
            </div>
          </div>

          {/* ══ SCROLLABLE BODY ══ */}
          <div className="px-8 py-6 space-y-6">

            {/* INPUTS SUMMARY (G3) — transparent line of inputs used */}
            {inputsSummary.length > 0 && (
              <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs rounded-xl px-4 py-3 ${
                darkMode ? "bg-gray-800/40 border border-gray-700/60 text-gray-400" : "bg-gray-50 border border-gray-200 text-gray-500"
              }`}>
                {inputsSummary.map((it, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1">
                    <span>{it.label}:</span>
                    <span className={`font-medium ${darkMode ? "text-gray-200" : "text-gray-800"}`}>
                      {it.value}{it.unit ? ` ${it.unit === "SAR" ? "ر.س" : it.unit}` : ""}
                    </span>
                    {idx < inputsSummary.length - 1 && <span className={darkMode ? "text-gray-600" : "text-gray-300"}>|</span>}
                  </span>
                ))}
              </div>
            )}

            {/* TARGET NOTICE (A2) */}
            {targetNotice && (
              <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded-xl flex items-start gap-2">
                <FiInfo className="text-amber-400 flex-shrink-0 mt-0.5" />
                <p className={`text-sm flex-1 ${darkMode ? "text-amber-300" : "text-amber-700"}`}>{targetNotice}</p>
              </div>
            )}

            {/* KPI GRID (G2) */}
            {displayKpis.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <FiBarChart2 className="text-blue-400" />
                  <h3 className={`${darkMode ? "text-white" : "text-gray-900"} font-semibold`}>{t("key_performance")}</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
  {displayKpis.map((row, i) => {
    const meta   = KPI_META[row.key];
    const rawNum = typeof row.value === "number";
    const isGood = meta && rawNum ? meta.good(row.value) : true;
    const isNeg  = rawNum && row.value < 0;
    return (
      <motion.div
        key={row.key}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 + i * 0.06 }}
        className={`${
          darkMode
            ? "bg-gray-800/60 border-gray-700/60 hover:border-gray-600"
            : "bg-white border-gray-200 hover:border-gray-300 shadow-sm"
        } border rounded-xl p-4 transition-colors`}
      >
        <div className="flex items-center gap-1.5 mb-2">
          <span className={`text-sm ${meta?.color || "text-blue-400"}`}>
            {meta?.icon || <FiTrendingUp />}
          </span>
          <p className={`${
            darkMode ? "text-gray-500" : "text-gray-400"
          } text-xs uppercase tracking-wide truncate`}>
            {row.label}
          </p>
        </div>
        <div className="flex items-end justify-between gap-1">
          <p className={`text-xl font-bold leading-none ${
            isNeg
              ? "text-red-400"
              : darkMode ? "text-white" : "text-gray-900"
          }`}>
            {formatDisplayValue(row)}
          </p>
          {rawNum && meta && (
            <span className={`text-xs flex-shrink-0 ${
              isGood ? "text-green-400" : "text-red-400"
            }`}>
              {isGood ? <FiTrendingUp /> : <FiTrendingDown />}
            </span>
          )}
        </div>
      </motion.div>
    );
  })}
</div>
              </section>
            )}

            {/* ANALYSIS */}
            {decision.reason && (() => {
              const localizedReason = getLocalizedReason(decision, lang);
              const showEnBadge     = lang === 'ar' && !decision.reasonAr;
              return (
                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <FiInfo className="text-blue-400" />
                    <h3 className={`${darkMode ? "text-white" : "text-gray-900"} font-semibold`}>{t("analysis")}</h3>
                  </div>
                  <div className={`${darkMode ? "bg-gray-800/40 border-gray-700/60" : "bg-gray-50 border-gray-200"} border rounded-xl p-5`}>
                    {showEnBadge && <span className={`inline-block text-xs ${darkMode ? "bg-gray-700 text-gray-400" : "bg-gray-200 text-gray-500"} px-2 py-0.5 rounded mb-2`}>EN</span>}
                    <p className={`${darkMode ? "text-gray-300" : "text-gray-700"} leading-relaxed`} dir={showEnBadge ? "ltr" : undefined}>{localizedReason}</p>
                  </div>
                </section>
              );
            })()}

            {/* RECOMMENDED ACTIONS */}
            {decision.nextActions?.length > 0 && (() => {
              const localizedActions = getLocalizedActions(decision, lang);
              return (
                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <FiZap className="text-yellow-400" />
                    <h3 className={`${darkMode ? "text-white" : "text-gray-900"} font-semibold`}>{t("recommended_actions")}</h3>
                  </div>
                  <div className={`${darkMode ? "bg-blue-500/5 border-blue-500/20" : "bg-blue-50 border-blue-200"} border rounded-xl p-5 space-y-3`}>
                    {localizedActions.map((action, i) => (
                      <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.07 }} className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-blue-600/30 text-blue-400 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                        <p className={`${darkMode ? "text-gray-300" : "text-gray-700"} text-sm leading-relaxed flex-1`}>{action}</p>
                      </motion.div>
                    ))}
                  </div>
                </section>
              );
            })()}

            {/* UPGRADE NOTICES */}
            {(!canShare() || !canDownloadPDF()) && (
              <div className="space-y-2">
                {!canShare() && (
                  <div className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-xl flex items-center gap-3">
                    <FiInfo className="text-blue-400 flex-shrink-0" />
                    <p className="text-blue-300 text-sm flex-1">{t("share_locked_msg")}{" "}<a href="/admin/plans" className="text-blue-400 hover:text-blue-300 underline font-medium">{t("upgrade_to_unlock")}</a></p>
                  </div>
                )}
                {!canDownloadPDF() && (
                  <div className="bg-purple-500/10 border border-purple-500/30 p-3 rounded-xl flex items-center gap-3">
                    <FiInfo className="text-purple-400 flex-shrink-0" />
                    <p className="text-purple-300 text-sm flex-1">{t("pdf_locked_msg")}{" "}<a href="/admin/plans" className="text-purple-400 hover:text-purple-300 underline font-medium">{t("upgrade_to_unlock")}</a></p>
                  </div>
                )}
              </div>
            )}

            {/* ACTION BUTTONS */}
            <div className={`flex flex-col sm:flex-row gap-3 pt-2 border-t ${darkMode ? "border-gray-700/60" : "border-gray-200"}`}>
              <button onClick={onClose}
                className={`flex-1 py-3 rounded-xl transition-colors text-sm font-medium ${darkMode ? "bg-gray-700/80 hover:bg-gray-700 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-900"}`}>
                {t("close")}
              </button>
              {canShare() && onShare ? (
                <button onClick={onShare} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl transition-colors flex items-center justify-center gap-2 font-semibold text-sm">
                  <FiShare2 />{t("share_decision")}
                </button>
              ) : (
                <button disabled className="flex-1 bg-gray-700/40 text-gray-500 py-3 rounded-xl flex items-center justify-center gap-2 cursor-not-allowed text-sm">
                  <FiShare2 />{t("share")} ({t("upgrade_required")})
                </button>
              )}
              {canDownloadPDF() ? (
                <button onClick={handlePDF} disabled={pdfGenerating}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white py-3 rounded-xl transition-colors flex items-center justify-center gap-2 font-semibold text-sm">
                  {pdfGenerating ? <><FiLoader className="animate-spin" />{t("generating_pdf")}</> : <><FiDownload />{t("download_pdf")}</>}
                </button>
              ) : (
                <button disabled className="flex-1 bg-gray-700/40 text-gray-500 py-3 rounded-xl flex items-center justify-center gap-2 cursor-not-allowed text-sm">
                  <FiDownload />PDF ({t("upgrade_required")})
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default DecisionResult;